import React, { useEffect } from 'react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { motion } from 'framer-motion';
import { Shield, Eye, Users, Home, Phone, AlertTriangle, Activity } from 'lucide-react';
export function PersonalSafetyPage() {
  useEffect(() => {
    document.title = "Personal Safety Tips | Walker's Martial Arts";
  }, []);
  const safetyTips = [{
    icon: Eye,
    title: 'Stay Aware of Your Surroundings',
    description: 'Always be alert to your environment and the people around you. Avoid distractions like your phone when in public spaces.'
  }, {
    icon: Users,
    title: 'Trust Your Instincts',
    description: 'If a situation feels off or makes you uncomfortable, trust your gut feelings and take action to remove yourself from it.'
  }, {
    icon: Home,
    title: 'Use Safe Routes',
    description: 'Choose well-lit, populated areas for walking, especially at night. Familiarize yourself with your surroundings and consider alternative routes to increase your safety.'
  }, {
    icon: Phone,
    title: 'Plan Ahead',
    description: "Wherever you go out, have a plan in place. Inform someone of your whereabouts and estimated return time, especially if you're heading to an unfamiliar area."
  }, {
    icon: Shield,
    title: 'Know Self-Defense Basics',
    description: 'Consider taking self-defense classes. Understanding basic techniques can empower you and help you react effectively in a threatening situation.'
  }, {
    icon: AlertTriangle,
    title: 'Keep Personal Items Secure',
    description: 'Be mindful of your belongings. Use bags that close securely, and avoid displaying valuables in public to minimize the risk of theft.'
  }, {
    icon: Activity,
    title: 'Stay Sober and Alert',
    description: 'Avoid alcohol or substances that can impair your judgment and lead to risky situations. Maintaining clarity enhances your decision-making capability.'
  }, {
    icon: Users,
    title: 'Utilize Technology',
    description: 'Leverage safety apps and features on your phone, such as location sharing or emergency contacts, to stay connected and protected.'
  }, {
    icon: Home,
    title: 'Have a Safety Plan',
    description: 'Develop a plan for emergencies. This should include contacts for support, places to go, and strategies for staying safe in various scenarios.'
  }, {
    icon: Shield,
    title: 'Educate Yourself on Local Resources',
    description: 'Be aware of local safety resources, including emergency numbers, nearby shelters, and self-defense groups. Having this information at hand can be invaluable.'
  }];
  return <div className="bg-black min-h-screen" data-id="element-1477">
      <Navigation data-id="element-1478" />

      {/* Hero Section with Image */}
      <div className="relative pt-20 pb-12 overflow-hidden" data-id="element-1479">
        <div className="absolute inset-0 z-0" data-id="element-1480">
          <img src="https://cdn.magicpatterns.com/uploads/uxeL1Dy5s4s1VdN8xyZUJW/safetly.png" alt="Personal Safety Tips" className="w-full h-full object-cover opacity-30" data-id="element-1481" />
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/80 to-black" data-id="element-1482" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12" data-id="element-1483">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-1484">
            <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6" data-id="element-1485">
              Personal <span className="text-[#00D4FF]" data-id="element-1486">Safety Tips</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl leading-relaxed" data-id="element-1487">
              Personal safety is a critical aspect of our daily lives, and
              awareness is the first step in ensuring it. Here are a few key
              strategies to enhance your personal safety:
            </p>
          </motion.div>
        </div>
      </div>

      {/* Safety Tips Grid */}
      <section className="py-20 bg-[#111]" data-id="element-1488">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1489">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1490">
            {safetyTips.map((tip, index) => <motion.div key={tip.title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-[#1a1a1a] p-8 rounded-2xl border border-gray-800 hover:border-[#00D4FF]/50 transition-colors" data-id="element-1491">
                <div className="flex items-start gap-6" data-id="element-1492">
                  <div className="flex-shrink-0" data-id="element-1493">
                    <div className="w-12 h-12 bg-[#00D4FF]/10 rounded-xl flex items-center justify-center" data-id="element-1494">
                      <tip.icon className="w-6 h-6 text-[#00D4FF]" data-id="element-1495" />
                    </div>
                  </div>
                  <div className="flex-1" data-id="element-1496">
                    <div className="flex items-baseline gap-3 mb-3" data-id="element-1497">
                      <span className="text-[#00D4FF] font-bold text-2xl" data-id="element-1498">
                        {index + 1}.
                      </span>
                      <h3 className="text-xl font-bold text-white" data-id="element-1499">
                        {tip.title}
                      </h3>
                    </div>
                    <p className="text-gray-400 leading-relaxed" data-id="element-1500">
                      {tip.description}
                    </p>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Closing Statement */}
      <section className="py-20 bg-black" data-id="element-1501">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1502">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-1503">
            <p className="text-xl text-gray-300 leading-relaxed mb-8" data-id="element-1504">
              Implementing these strategies can help improve your awareness and
              response to potential safety threats. Personal safety is an
              ongoing commitment; it is essential to remain vigilant and
              proactive.
            </p>
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#00D4FF]/10 border border-[#00D4FF]/30 text-[#00D4FF]" data-id="element-1505">
              <Shield className="w-5 h-5" data-id="element-1506" />
              <span className="font-semibold" data-id="element-1507">Stay Safe, Stay Aware</span>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer data-id="element-1508" />
    </div>;
}